import { FC, useState, useEffect, useCallback } from "react"
import {
  MessageSquareDot,
  Loader2,
  RefreshCw,
  Trash2,
  Pencil,
  CheckCircle2,
  Circle,
  Plus,
  Sparkles
} from "lucide-react"
import { Brand } from "../../../../types"
import BrandAutocomplete from "../../brand-autocomplete"
import { PrimaryBtn } from "../../buttons"
import Tabs from "../../tabs"
import aiAmendmentsApi, { Amendment } from "../../../../api/ai-amendments"
import { useAppDispatch } from "../../../hooks"
import { pushToast } from "../../../../redux/slices/notifications"

type TabId = "new" | "existing"

interface Props {
  brands: Brand[]
}

const AiPromptAmendments: FC<Props> = ({ brands }) => {
  const dispatch = useAppDispatch()
  const [activeTab, setActiveTab] = useState<TabId>("new")
  const [selectedBrandId, setSelectedBrandId] = useState<string>("")
  const [newText, setNewText] = useState<string>("")
  const [isCreating, setIsCreating] = useState(false)
  const [amendments, setAmendments] = useState<Amendment[]>([])
  const [isLoadingList, setIsLoadingList] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [togglingId, setTogglingId] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editText, setEditText] = useState<string>("")
  const [savingEditId, setSavingEditId] = useState<string | null>(null)

  const activeCount = amendments.filter(a => a.enabled).length

  const tabs = [
    { id: "new" as TabId, label: "New" },
    {
      id: "existing" as TabId,
      label:
        amendments.length > 0 ? `Existing (${amendments.length})` : "Existing"
    }
  ]

  const fetchAmendments = useCallback(async () => {
    if (!selectedBrandId) return
    setIsLoadingList(true)
    try {
      const data = await aiAmendmentsApi.list("brand", selectedBrandId)
      setAmendments(data)
    } catch (error: any) {
      dispatch(
        pushToast({
          msg: "Failed to load amendments",
          theme: "danger",
          timeout: 5000
        })
      )
    } finally {
      setIsLoadingList(false)
    }
  }, [selectedBrandId, dispatch])

  useEffect(() => {
    setAmendments([])
    setEditingId(null)
    setEditText("")
    if (selectedBrandId) {
      fetchAmendments()
    }
  }, [selectedBrandId]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async () => {
    if (!selectedBrandId || !newText.trim() || isCreating) return
    setIsCreating(true)
    try {
      await aiAmendmentsApi.create({
        level: "brand",
        levelId: selectedBrandId,
        text: newText.trim()
      })
      dispatch(
        pushToast({
          msg: "Amendment added successfully",
          theme: "success",
          timeout: 3000
        })
      )
      setNewText("")
      await fetchAmendments()
      setActiveTab("existing")
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to add amendment"
      dispatch(pushToast({ msg, theme: "danger", timeout: 5000 }))
    } finally {
      setIsCreating(false)
    }
  }

  const handleToggle = async (amendment: Amendment) => {
    setTogglingId(amendment._id)
    try {
      await aiAmendmentsApi.update(amendment._id, {
        enabled: !amendment.enabled
      })
      await fetchAmendments()
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update amendment"
      dispatch(pushToast({ msg, theme: "danger", timeout: 5000 }))
    } finally {
      setTogglingId(null)
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this amendment?"))
      return
    setDeletingId(id)
    try {
      await aiAmendmentsApi.delete(id)
      dispatch(
        pushToast({ msg: "Amendment deleted", theme: "success", timeout: 3000 })
      )
      await fetchAmendments()
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete amendment"
      dispatch(pushToast({ msg, theme: "danger", timeout: 5000 }))
    } finally {
      setDeletingId(null)
    }
  }

  const handleSaveEdit = async (id: string) => {
    setSavingEditId(id)
    try {
      await aiAmendmentsApi.update(id, { text: editText })
      dispatch(
        pushToast({ msg: "Amendment updated", theme: "success", timeout: 3000 })
      )
      setEditingId(null)
      setEditText("")
      await fetchAmendments()
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update amendment"
      dispatch(pushToast({ msg, theme: "danger", timeout: 5000 }))
    } finally {
      setSavingEditId(null)
    }
  }

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return ""
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    })
  }

  return (
    <div className="bg-white border border-neutral-200 rounded-lg col-span-1 lg:col-span-2">
      {/* Header */}
      <div className="px-6 py-5 border-b border-neutral-100 bg-neutral-50 rounded-t-lg">
        <div className="flex items-center justify-between gap-6">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
              <MessageSquareDot className="w-5 h-5 text-primary-600" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900">
                AI Prompt Amendments
              </h3>
              <p className="text-sm text-gray-500">
                Text snippets appended to the AI advisor's system prompt, scoped
                per brand
              </p>
            </div>
          </div>

          {/* Brand selector in header */}
          <div className="flex-shrink-0 w-72">
            <BrandAutocomplete
              selected={selectedBrandId}
              brands={brands}
              direction="vertical"
              hideLabel
              onChange={id => setSelectedBrandId(id)}
            />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-6 border-b border-neutral-100">
        <Tabs tabs={tabs} currentTab={activeTab} onChange={setActiveTab} />
      </div>

      {/* New tab */}
      {activeTab === "new" && (
        <div className="p-6">
          {!selectedBrandId ? (
            <div className="flex flex-col items-center justify-center py-12 rounded-xl border border-dashed border-gray-200 bg-gray-50 text-gray-400">
              <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mb-3">
                <Sparkles className="w-5 h-5 text-gray-400" />
              </div>
              <p className="text-sm font-medium text-gray-500">
                Select a brand to add an amendment
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Use the brand picker in the top-right corner
              </p>
            </div>
          ) : (
            <div className="max-w-2xl space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Amendment Text
                </label>
                <textarea
                  rows={5}
                  value={newText}
                  onChange={e => setNewText(e.target.value)}
                  placeholder="e.g. Do not discuss closing costs — always defer to the loan officer if that topic comes up."
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none leading-relaxed"
                />
                <p className="mt-1.5 text-xs text-gray-400">
                  This text is appended verbatim to the AI advisor's system
                  prompt for the selected brand.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <PrimaryBtn
                  onClick={handleCreate}
                  disabled={!selectedBrandId || !newText.trim() || isCreating}
                >
                  {isCreating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                      Adding…
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-1.5" />
                      Add Amendment
                    </>
                  )}
                </PrimaryBtn>
                {newText.trim() && (
                  <button
                    onClick={() => setNewText("")}
                    className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Existing tab */}
      {activeTab === "existing" && (
        <div className="p-6">
          {!selectedBrandId ? (
            <div className="flex flex-col items-center justify-center py-12 rounded-xl border border-dashed border-gray-200 bg-gray-50 text-gray-400">
              <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mb-3">
                <MessageSquareDot className="w-5 h-5 text-gray-400" />
              </div>
              <p className="text-sm font-medium text-gray-500">
                Select a brand to view amendments
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Use the brand picker in the top-right corner
              </p>
            </div>
          ) : isLoadingList ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
            </div>
          ) : (
            <>
              {/* Toolbar */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2.5">
                  {amendments.length > 0 ? (
                    <>
                      <span className="text-sm text-gray-600">
                        {amendments.length} amendment
                        {amendments.length !== 1 ? "s" : ""}
                      </span>
                      <span className="text-gray-300">·</span>
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-100 px-2 py-0.5 rounded-full">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                        {activeCount} active
                      </span>
                      {amendments.length - activeCount > 0 && (
                        <>
                          <span className="text-gray-300">·</span>
                          <span className="text-xs text-gray-400">
                            {amendments.length - activeCount} disabled
                          </span>
                        </>
                      )}
                    </>
                  ) : (
                    <span className="text-sm text-gray-400">No amendments</span>
                  )}
                </div>
                <button
                  onClick={() => {
                    setIsLoadingList(true)
                    fetchAmendments()
                  }}
                  disabled={isLoadingList}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                >
                  <RefreshCw
                    className={`w-3.5 h-3.5 ${
                      isLoadingList ? "animate-spin" : ""
                    }`}
                  />
                  Refresh
                </button>
              </div>

              {amendments.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 rounded-xl border border-dashed border-gray-200 bg-gray-50">
                  <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mb-3">
                    <MessageSquareDot className="w-5 h-5 text-gray-400" />
                  </div>
                  <p className="text-sm font-medium text-gray-500">
                    No amendments configured for this brand
                  </p>
                  <button
                    onClick={() => setActiveTab("new")}
                    className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add the first amendment
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {amendments.map(amendment => (
                    <div
                      key={amendment._id}
                      className={`rounded-lg border overflow-hidden transition-all duration-150 ${
                        editingId === amendment._id
                          ? "border-primary-300 shadow-sm ring-1 ring-primary-100"
                          : "border-neutral-200 hover:border-neutral-300"
                      } ${
                        !amendment.enabled && editingId !== amendment._id
                          ? "opacity-60"
                          : ""
                      }`}
                    >
                      {/* Status accent bar */}
                      <div
                        className={`h-0.5 transition-colors ${
                          amendment.enabled ? "bg-green-400" : "bg-gray-200"
                        }`}
                      />

                      {editingId === amendment._id ? (
                        <div className="p-4 space-y-3 bg-primary-50/30">
                          <textarea
                            rows={4}
                            value={editText}
                            onChange={e => setEditText(e.target.value)}
                            autoFocus
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none leading-relaxed bg-white"
                          />
                          <div className="flex items-center gap-2">
                            <PrimaryBtn
                              onClick={() => handleSaveEdit(amendment._id)}
                              disabled={
                                savingEditId === amendment._id ||
                                !editText.trim()
                              }
                              className="text-sm"
                            >
                              {savingEditId === amendment._id
                                ? "Saving…"
                                : "Save Changes"}
                            </PrimaryBtn>
                            <button
                              onClick={() => {
                                setEditingId(null)
                                setEditText("")
                              }}
                              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-white border border-gray-200 rounded-lg transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start gap-3 px-4 py-3.5">
                          {/* Toggle button */}
                          <button
                            onClick={() => handleToggle(amendment)}
                            disabled={!!togglingId || !!editingId}
                            className="mt-0.5 flex-shrink-0 disabled:opacity-40 transition-all hover:scale-110"
                            title={
                              amendment.enabled
                                ? "Click to disable"
                                : "Click to enable"
                            }
                          >
                            {togglingId === amendment._id ? (
                              <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                            ) : amendment.enabled ? (
                              <CheckCircle2 className="w-5 h-5 text-green-500" />
                            ) : (
                              <Circle className="w-5 h-5 text-gray-300 hover:text-gray-400" />
                            )}
                          </button>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <p
                              className={`text-sm leading-relaxed line-clamp-2 ${
                                amendment.enabled
                                  ? "text-gray-800"
                                  : "text-gray-500"
                              }`}
                            >
                              {amendment.text}
                            </p>
                            <div className="flex items-center gap-2 mt-1.5">
                              {amendment.enabled ? (
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                                  <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                  Active
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">
                                  Disabled
                                </span>
                              )}
                              <span className="text-gray-300 text-xs">·</span>
                              <span className="text-xs text-gray-400">
                                {formatDate(
                                  amendment.updatedAt ?? amendment.createdAt
                                )}
                              </span>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-0.5 flex-shrink-0 ml-2">
                            <button
                              onClick={() => {
                                setEditingId(amendment._id)
                                setEditText(amendment.text)
                              }}
                              disabled={!!editingId || !!togglingId}
                              className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors disabled:opacity-40"
                              title="Edit"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleDelete(amendment._id)}
                              disabled={
                                deletingId === amendment._id ||
                                !!editingId ||
                                !!togglingId
                              }
                              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors disabled:opacity-40"
                              title="Delete"
                            >
                              {deletingId === amendment._id ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Trash2 className="w-3.5 h-3.5" />
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default AiPromptAmendments
